<template>
  <div>
    <header class="fixed left-0 right-0 top-0 z-30 flex h-14 items-center justify-between border-b border-zinc-200 bg-white/95 px-4 backdrop-blur lg:left-64 lg:justify-end lg:px-8">
      <Link href="/" class="flex min-w-0 items-center lg:hidden" aria-label="Ir para a página inicial">
        <img :src="logoStacklab" class="h-10 w-auto object-contain brightness-0" alt="StackLab" />
      </Link>
      <div class="flex items-center gap-1 lg:hidden">
        <button type="button" class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-zinc-600 transition hover:bg-zinc-50" aria-label="Buscar na documentação" @click="open = true">
          <MagnifyingGlass class="h-5 w-5" />
        </button>
        <button type="button" class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-zinc-200 text-zinc-600 transition hover:border-zinc-300 hover:bg-zinc-50" aria-label="Abrir menu de navegação" @click="openMobileSidebar">
          <List class="h-5 w-5" />
        </button>
      </div>
      <button type="button" class="hidden min-w-0 items-center gap-2.5 rounded-md border border-zinc-200 bg-zinc-50 px-2.5 py-1.5 text-left text-xs text-zinc-500 transition hover:border-zinc-300 hover:bg-white lg:flex lg:w-full lg:max-w-xs" @click="open = true">
        <MagnifyingGlass class="h-4 w-4" />
        <span>Buscar na documentação...</span>
        <kbd class="ml-auto rounded border border-zinc-200 bg-white px-1.5 py-0.5 text-[10px] text-zinc-400">⌘ K</kbd>
      </button>
    </header>
    <div v-if="open" class="fixed inset-0 z-50 flex items-start justify-center bg-emerald-950/30 px-4 pt-24 backdrop-blur-sm" @click.self="open = false">
      <section class="w-full max-w-2xl overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-xl" role="dialog" aria-modal="true" aria-label="Busca global">
        <div class="flex items-center gap-3 border-b border-zinc-200 px-4">
          <MagnifyingGlass class="h-5 w-5 text-zinc-400" />
          <input ref="searchInput" v-model="query" type="search" autofocus placeholder="Buscar manuais, categorias, grupos ou endpoints" class="h-14 min-w-0 flex-1 border-0 bg-transparent text-sm text-zinc-950 outline-none focus:ring-0" />
          <button type="button" class="text-xs text-zinc-400 hover:text-zinc-950" @click="open = false">Esc</button>
        </div>
        <div class="max-h-[60vh] overflow-y-auto p-2">
          <Link v-for="(result, index) in results" :key="`${result.type}-${result.href}-${result.title}`" :href="result.href" :class="selectedIndex === index ? 'bg-zinc-50' : ''" class="block rounded-lg px-3 py-3 hover:bg-zinc-50" @click="handleResultClick(result, $event)">
            <div class="flex items-start gap-3">
              <component :is="resultIcon(result.type)" weight="duotone" class="mt-0.5 h-4 w-4 shrink-0 text-zinc-500" aria-hidden="true" />
              <div class="min-w-0">
                <p class="text-sm font-medium text-zinc-950">{{ result.title }}</p>
                <p class="mt-1 text-xs text-zinc-500">{{ result.type }}<span v-if="result.description"> · {{ result.description }}</span></p>
              </div>
            </div>
          </Link>
          <p v-if="query && !results.length" class="px-3 py-8 text-center text-sm text-zinc-500">Nenhum resultado encontrado.</p>
          <p v-if="!query" class="px-3 py-8 text-center text-sm text-zinc-500">Digite para buscar em toda a documentação.</p>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Link, router } from "@inertiajs/vue3";
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { PhBookOpen as BookOpen, PhGraduationCap as GraduationCap, PhList as List, PhMagnifyingGlass as MagnifyingGlass, PhSquaresFour as SquaresFour } from "@phosphor-icons/vue";
import logoStacklab from "../../assets/logo_stacklab.png";
import { manualCategories, manualCatalog } from "../../data/manualCatalog";

type Result = { title: string; description?: string; type: string; href: string };
const open = ref(false);
const query = ref("");
const selectedIndex = ref(0);
const apiResults = ref<Result[]>([]);
const searchInput = ref<HTMLInputElement>();
const openMobileSidebar = () => { window.dispatchEvent(new CustomEvent("toggle-mobile-sidebar")); };
const manualResults = manualCatalog.flatMap((solution) => solution.manuals.map((manual) => ({ title: manual.title, description: solution.name, type: "Manual", href: `/manuais/${manual.slug}` })));
const categoryResults = manualCategories.map((category) => ({ title: category.name, description: category.description, type: "Categoria", href: `/manuais/categoria/${category.slug}` }));
const resultIcon = (type: string) => type === "Manual" ? GraduationCap : type === "Categoria" ? SquaresFour : BookOpen;
const results = computed(() => {
  const term = query.value.trim().toLocaleLowerCase();
  if (!term) return [];
  return [...manualResults, ...categoryResults, ...apiResults.value].filter((item) => `${item.title} ${item.description || ""} ${item.type}`.toLocaleLowerCase().includes(term)).slice(0, 30);
});
watch(query, () => { selectedIndex.value = 0; });
const activateResult = (result: Result) => {
  open.value = false;
  if (result.type === "Grupo de API" || result.type === "Endpoint") {
    window.location.assign(result.href);
  } else {
    router.visit(result.href);
  }
};
const handleResultClick = (result: Result, event: MouseEvent) => {
  event.preventDefault();
  activateResult(result);
};
watch(open, async (isOpen) => {
  if (isOpen) {
    await nextTick();
    searchInput.value?.focus();
  }
});
const onKeydown = (event: KeyboardEvent) => {
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") { event.preventDefault(); open.value = true; return; }
  if (!open.value) return;
  if (event.key === "Escape") { open.value = false; return; }
  if (!results.value.length) return;
  if (event.key === "ArrowDown") { event.preventDefault(); selectedIndex.value = (selectedIndex.value + 1) % results.value.length; }
  if (event.key === "ArrowUp") { event.preventDefault(); selectedIndex.value = (selectedIndex.value - 1 + results.value.length) % results.value.length; }
  if (event.key === "Enter") { event.preventDefault(); activateResult(results.value[selectedIndex.value]); }
};
onMounted(async () => {
  window.addEventListener("keydown", onKeydown);
  const response = await fetch("/api/search-index");
  if (response.ok) apiResults.value = await response.json();
});
onUnmounted(() => { window.removeEventListener("keydown", onKeydown); });
</script>
